import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";

import { useFlags } from "@stadium/services/launch-darkly/use-flags";
import { useQuery } from "@stadium/services/apollo-client";
import { withFeatureFlags } from "@stadium/services/launch-darkly";

import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useCheckoutContext } from "@contexts/checkout-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import useConfirmationModal from "@hooks/cart/use-confirmation-modal";
import useEmptyCart from "@hooks/cart/use-empty-cart";
import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";

import AccordionAllCollapsable from "@stadium/ui/src/components/accordion/accordion-all-collapsable";
import { PageLoader } from "@stadium/ui/src/components/loaders/loaders";

import { EmptyBag } from "@sharedComponents/cart/common";
import LineItemCard from "@sharedComponents/cart/line-item-card";
import { InternalButton } from "@sharedComponents/buttons";
import RequestMockupButton from "@sharedComponents/buttons/request-mockup";

import {
  getCartItemsCount,
  getCartOrder,
  getCartOrderFetching,
  getCartOrderId,
  getCartOrderLineItems,
  getCartItemLogoIds,
} from "@store/selectors/cart";
import { getStoreName } from "@selectors/store";

import { updateCart, updateCartOrder } from "@slices/cart";
import { getCountry } from "@store/selectors/country";
import { getStoreIsOrderFetching, getStoreNumber } from "@store/selectors/store";

import { fetchVariantsList } from "@graphql/queries/stores/products/fetch-variant-list";

import {
  computeExternalSourceIds,
  generateLockerItems,
  getUpdatedLineItemFromVariants,
} from "@helpers/orders/cart";
import { isGlobalCountry } from "@stadium/helpers/country/utils";
import { getMenuRoute } from "@constants/routes";
import { removeItemsConfirmation } from "@constants/confirmation-modal";

import { variantListLimitQueryFieldsFlag } from "@stadium/services/launch-darkly/constants";
import { ASSET_KINDS } from "@stadium/shared/src/constants/logo";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_CLEARED } from "@stadium/services/analytics/constants/default";
import { ITEM_PRODUCT_TYPES } from "@constants/orders/cart";
import { SHIP_DURATION } from "@stadium/constants/locker";

import styles from "./main.module.scss";

const CartListingForLocker = ({
  editable = true,
  className = "",
  renderWalletFunds = () => {},
}) => {
  const dispatch = useDispatch();
  const cartOrder = useSelector(getCartOrder);
  const lineItems = useSelector(getCartOrderLineItems);
  const itemsCount = useSelector(getCartItemsCount);
  const orderId = useSelector(getCartOrderId);
  const orderFetching = useSelector(getCartOrderFetching);
  const storeNumber = useSelector(getStoreNumber);
  const isStoreOrderFetching = useSelector(getStoreIsOrderFetching);
  const country = useSelector(getCountry);
  const { storeBasePath } = useStoreContext();
  const logoIds = useSelector(getCartItemLogoIds);

  const storeName = useSelector(getStoreName);

  const { [variantListLimitQueryFieldsFlag]: variantListLimitQueryFieldsFlagEnabled } = useFlags();

  const withAnalytics = useWithAnalytics();

  const { volumeDiscountProductsVariables, isSendToOthers } = useSendItemsContext();

  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const {
    cartItemsQuantity,
    orderCurrencyCode,
    isAnyItemWithoutDigitalAsset,
    setIsAnyItemWithoutDigitalAsset,
  } = useCheckoutContext();

  const [isCartListingOpen, setIsCartListingOpen] = useState(false);
  const toggleCartListingAccordion = () => setIsCartListingOpen((prev) => !prev);

  const { externalSourceIds, catalogExternalStoreProductMappings } = useMemo(
    () => computeExternalSourceIds({ lineItems }),
    [lineItems],
  );

  const fetchDigitalAssets = logoIds?.length > 0;

  const globalZone = isGlobalCountry(country);
  const variables = {
    globalZone,
    country: globalZone ? "" : country.iso,
    storeNumber,
    externalSourceIds,
    catalogExternalStoreProductMappings,
    ...volumeDiscountProductsVariables,
    fetchDigitalAssets,
    ...(fetchDigitalAssets && {
      ids: logoIds,
      kind: ASSET_KINDS.logo,
    }),
    limitQueryFields: Boolean(variantListLimitQueryFieldsFlagEnabled) && !isSendToOthers,
  };

  const { toggleConfirmationModal, renderConfirmationModal } =
    useConfirmationModal(removeItemsConfirmation);

  const { emptyCart, loading: emptyingCart } = useEmptyCart({ successCb: toggleConfirmationModal });
  const { deleteItemFromCart, updateItemInCart, loading } = useBatchPopulateItems();

  const { data: variantData, loading: variantDataloading } = useQuery(fetchVariantsList, {
    variables,
    skip: !variables.externalSourceIds,
    fetchPolicy: "cache-and-network",
  });

  const variantsMapping = useMemo(() => {
    if (variantDataloading || !variantData) {
      return {};
    } else {
      return groupBy(variantData.variants, "externalId");
    }
  }, [variantDataloading, variantData]);

  //to update images of line items and product sets in redux store
  //cartOrder does needs to be added in the dependency array to avoid infinite loop
  useEffect(() => {
    if (!isEmpty(variantsMapping)) {
      const { lineItems } = cartOrder;
      const updatedLineItems = lineItems.map(getUpdatedItem);
      const { customizable, designed } = generateLockerItems(updatedLineItems, variantsMapping);
      const allItems = { ...customizable, ...designed };
      dispatch(
        updateCart({
          ...cartOrder,
          lineItems: updatedLineItems.map((lineItem) => ({
            ...Object.values(allItems).find(({ id: lineItemId }) => lineItemId === lineItem.id),
            ...lineItem,
          })),
        }),
      );
    }
    // to make sure data is in sync in global state after product qty change
  }, [variantsMapping, loading]);

  const disabled = emptyingCart || loading;

  const lineItemsMapping = useMemo(
    () => generateLockerItems(lineItems, variantDataloading ? null : variantsMapping),
    [lineItems, variantsMapping, variantDataloading],
  );

  const { designed, customizable } = lineItemsMapping;
  const hasDesigned = Object.keys(designed).length > 0;
  const hasCustomizable = Object.keys(customizable).length > 0;

  useEffect(() => {
    dispatch(updateCartOrder({ hasCustomizable, hasDesigned }));
  }, [hasCustomizable, hasDesigned, dispatch, lineItems]);

  useEffect(() => {
    if (variantDataloading || loading) return;
    if (!isEmpty(customizable)) {
      const isAnyItemWithoutDigitalAsset = Object.values(customizable).some(
        (item) => !item.lineItemCustomization,
      );
      setIsAnyItemWithoutDigitalAsset(isAnyItemWithoutDigitalAsset);
    } else {
      setIsAnyItemWithoutDigitalAsset(false);
    }
  }, [variantDataloading, loading, customizable]);

  const removeProductFromCart = ({ lineItem }) => {
    const { productIds, storeId } = lineItem;
    const payload = productIds.map((id) => ({
      product_id: id,
      quantity: 0,
      product_type: ITEM_PRODUCT_TYPES.product,
      store_id: storeId,
    }));
    deleteItemFromCart(payload);
  };

  const updateProductInCart = (product) => {
    const { variantLineItemIds, lineItemCustomization } = product;

    const payload = lineItems.reduce((acc, lineItem) => {
      if (variantLineItemIds?.includes(lineItem.id)) {
        const { productId, quantity, storePrice, price, storeId, vendorId } = lineItem;
        acc.push({
          product_id: productId,
          quantity,
          storePrice,
          price,
          product_type: ITEM_PRODUCT_TYPES.product,
          store_id: storeId,
          vendor_id: vendorId,
          ...(!isEmpty(lineItemCustomization) && lineItemCustomization),
        });
      }
      return acc;
    }, []);

    updateItemInCart(payload);
  };

  const getUpdatedItem = (item) => getUpdatedLineItemFromVariants(item, variantsMapping);

  const removeAllItemsHandler = () => {
    withAnalytics(undefined, {
      track: true,
      action: ANALYTICS_EVENT_ACTION_CLEARED,
      eventProperties: { total_items: itemsCount },
    })();
    emptyCart(orderId);
  };

  const renderProducts = ({ products, intent = "default", isCustomizable = false }) => {
    return products.map((item) => {
      const digitalAssetsData = variantData?.digitalAssets?.nodes?.find(
        (asset) => +asset.id === item.lineItemCustomization?.logoId,
      );
      const lineItemWithDigitalAssetData = {
        ...item,
        digitalAssetsData,
      };

      const updatedItem = getUpdatedItem(lineItemWithDigitalAssetData);
      return (
        <LineItemCard
          key={item.id}
          intent={intent}
          editable={editable}
          disabled={disabled}
          lineItem={updatedItem}
          removeProductFromCart={removeProductFromCart}
          onChangeQuantity={updateProductInCart}
          onUpdateLogo={updateProductInCart}
          orderCurrencyCode={orderCurrencyCode}
          isCustomizable={isCustomizable}
        />
      );
    });
  };

  const renderRemoveAllItemsCta = () =>
    editable && (
      <div className={`${styles.colAutoLink} ${styles.removeLinkBlock}`}>
        {hasCustomizable && !isAnyItemWithoutDigitalAsset && (
          <RequestMockupButton
            lineItemsMapping={lineItemsMapping}
            variantsMapping={variantsMapping}
            storeName={storeName}
            storeNumber={storeNumber}
          />
        )}

        <InternalButton
          styleType="link"
          className={`${styles.removeLink} ${styles.withLH32} ${styles.removeLinkMargin}`}
          onClick={toggleConfirmationModal}
        >
          Remove All
        </InternalButton>
      </div>
    );

  if ((isStoreOrderFetching && !isGuestOrPreviewMode) || orderFetching || variantDataloading)
    return (
      <div>
        <PageLoader wrapperClass="pageLoaderwrap min-h-150" />
      </div>
    );

  if (itemsCount === 0) return <EmptyBag menuPath={getMenuRoute({ storeBasePath })} />;

  const renderLockerCartListing = () => {
    return (
      <div className={className}>
        {editable ? (
          <>
            {renderRemoveAllItemsCta()}
            {hasDesigned && (
              <dl className={styles.dl}>
                <dt className={styles.brandHeading}>Ships in {SHIP_DURATION}</dt>
                {renderProducts({ products: Object.values(designed) })}
              </dl>
            )}

            {hasCustomizable && (
              <dl className={styles.dl}>
                {renderWalletFunds()}
                {renderProducts({ products: Object.values(customizable), isCustomizable: true })}
              </dl>
            )}
            {renderConfirmationModal({
              loading: emptyingCart,
              onConfirmHandler: removeAllItemsHandler,
            })}
          </>
        ) : (
          <>
            {hasDesigned && <div>{renderProducts({ products: Object.values(designed) })}</div>}
            {hasCustomizable && (
              <div>
                {renderProducts({ products: Object.values(customizable), isCustomizable: true })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (editable) return renderLockerCartListing();

  return (
    <AccordionAllCollapsable
      id="locker-listing"
      isOpen={isCartListingOpen}
      handleAccordianToggle={toggleCartListingAccordion}
      accordionCustomClass={styles.accordion}
      titleContent={
        <div className={styles.accordionHeader}>
          Your Items ({cartItemsQuantity})
          <div className={styles.collapseIconBlock}>
            <span
              className={` ${styles.arrowIcon} ${
                isCartListingOpen ? styles.iconUp : styles.iconDown
              }`}
            />
          </div>
        </div>
      }
      bodyContent={<div className={styles.collapseBlock}>{renderLockerCartListing()}</div>}
    />
  );
};

export default withFeatureFlags(CartListingForLocker);
